// Which variant the Test already holds is a report's finding whose description the answer withheld
// (TGCLOUD-1868, plan D3; review of the view). No React, no requests.
//
// THE APPLICATION MERGES, NEVER DUPLICATES (applyProposal). It finds the Test's own copy of a
// report's variant by what both describe (testRecordModel.sameVariant): the same finding, or the
// same gene and description. But a description the reader was not confident of reaches the view
// EMPTY (its readingDigest in `lowConfidence`), so a variant the Test holds, entered by hand from an
// emailed copy before the PDF was attached, matched nothing: the report's finding was added beside
// it, and once its c. was typed as printed the Test held the same variant twice.
//
// So, before the reading is applied, each finding whose c., g. or p. is withheld is compared with
// the Test's variants of the same gene that carry no finding of their own: the Test's description,
// as written and with its reference sequence cut off (testRecordModel.hgvsKeyOf), is digested the
// way the reading's was (readingDigest.matchesReading) and compared with what was read and what the
// report prints. A match is the same variant: the application merges into it (applyProposal
// `matched`), and the withheld description is still emptied there, to be typed and compared like
// any other. Each Test variant answers for one finding.
//
//   needsWithheldMatching({ reading, form })   whether there is anything to compare at all; when
//                                              not, the reading is applied at once, as before
//   withheldMatchesOf({ reading, form })        Promise<Map<findingId, index in form.variants>>

import {
  geneKeyOf,
  hgvsKeyOf
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/test-record/testRecordModel';
import { matchesReading } from './readingDigest';

const DESCRIPTIONS = Object.freeze([
  ['variantC', 'c.'],
  ['variantG', 'g.'],
  ['variantP', 'p.']
]);

const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const hasText = (v) => typeof v === 'string' && v.trim() !== '';

// Each finding of the reading with a withheld description that was read: [{ findingId, gene,
// withheld: [{ field, prefix, low }] }].
function findingsToMatch(reading) {
  const low = new Map(
    (Array.isArray(reading?.lowConfidence) ? reading.lowConfidence : [])
      .filter((l) => isPlain(l) && typeof l.path === 'string')
      .map((l) => [l.path, l])
  );
  const out = [];
  (reading?.proposal?.variants || []).forEach((v, i) => {
    if (!isPlain(v) || !hasText(v.findingId)) return;
    const gene = geneKeyOf(v.gene);
    const withheld = DESCRIPTIONS.map(([field, prefix]) => ({
      field,
      prefix,
      low: low.get(`variants.${i}.${field}`)
    })).filter(
      (d) => d.low && hasText(d.low.readingDigest) && !hasText(v[d.field])
    );
    if (gene && withheld.length) {
      out.push({ findingId: v.findingId, gene, withheld });
    }
  });
  return out;
}

// The Test's variants that could be a finding: of its gene, describing something, and no finding's.
const candidatesOf = (form, gene) =>
  (Array.isArray(form?.variants) ? form.variants : [])
    .map((v, index) => ({ v, index }))
    .filter(
      ({ v }) =>
        isPlain(v) &&
        !hasText(v.findingId) &&
        geneKeyOf(v.gene) === gene &&
        DESCRIPTIONS.some(([field]) => hasText(v[field]))
    );

/** Is there a withheld description to compare with a variant the Test holds? */
export function needsWithheldMatching({ reading, form }) {
  return findingsToMatch(reading).some(
    (f) => candidatesOf(form, f.gene).length > 0
  );
}

// The Test's description as written, and with its reference sequence cut off.
const spellingsOf = (value, prefix) =>
  [value.trim(), hgvsKeyOf(value, prefix)].filter(
    (s, i, all) => hasText(s) && all.indexOf(s) === i
  );

/**
 * The Test's variant each finding with a withheld description is, by digest (see the header).
 *
 * @param {{ reading: object, form: object }} args  the intake (or hand-off) answer, and the Test
 *   form as the reading arrives
 * @returns {Promise<Map<string, number>>}  findingId -> index in form.variants
 */
export async function withheldMatchesOf({ reading, form }) {
  const out = new Map();
  const taken = new Set();
  for (const finding of findingsToMatch(reading)) {
    for (const { v, index } of candidatesOf(form, finding.gene)) {
      if (taken.has(index)) continue;
      let same = false;
      for (const { field, prefix, low } of finding.withheld) {
        if (!hasText(v[field])) continue;
        for (const value of spellingsOf(v[field], prefix)) {
          const matched = await matchesReading({
            runId: reading.runId,
            path: low.path,
            value,
            readingDigest: low.readingDigest,
            printedDigest: low.printedDigest || null
          });
          if (matched === true) {
            same = true;
            break;
          }
        }
        if (same) break;
      }
      if (same) {
        out.set(finding.findingId, index);
        taken.add(index);
        break;
      }
    }
  }
  return out;
}
